import type {
  BuildSearchParamsProps,
  GetListParamsFromSearchProps,
  GetListParamProps,
  SearchContainsAtLeastOneListParamsProps,
  CheckIfListIsActiveProps,
} from "./list-params.type";
import type { ListPageNumber, ListParams } from "../list.type";
import type {
  TableViewFilters,
  TableViewPageSize,
  TableViewParams,
  TableViewSort,
} from "@/entities/table-view";

import isEqual from "lodash/isEqual";

import { isEmptyObject } from "@/utilities/object";

import {
  FILTER_KEY,
  PAGE_KEY,
  PER_PAGE_KEY,
  SORT_KEY,
} from "./list-params.constant";

export const checkIfListIsActive = ({
  contextTabKey,
  contextTabValue,
  locationState,
  searchParams,
}: CheckIfListIsActiveProps) => {
  // If current page is a background page, the list is not active
  if (locationState?.isBackgroundLocation) return false;

  // If there is no tab key in location search, we consider the list as active
  if (!contextTabKey || !searchParams.has(contextTabKey)) return true;

  // If there is a tab key in location search, the location tab must be the same as the contextTab
  return searchParams.get(contextTabKey) === contextTabValue;
};

export const searchContainsAtLeastOneListParams = ({
  searchParams,
  storageKey,
}: SearchContainsAtLeastOneListParamsProps) => {
  const search = searchParams.toString();
  return search?.includes(storageKey);
};

const sortIsEqual = (sortA?: TableViewSort, sortB?: TableViewSort) => {
  if (!sortA?.length && !sortB?.length) return true;
  return isEqual(sortA, sortB);
};

const filtersIsEqual = (
  filtersA?: TableViewFilters,
  filtersB?: TableViewFilters
) => {
  if (
    (!filtersA || isEmptyObject(filtersA)) &&
    (!filtersB || isEmptyObject(filtersB))
  ) {
    return true;
  }
  return isEqual(filtersA, filtersB);
};

const pageSizeIsEqual = (
  pageSizeA?: TableViewPageSize,
  pageSizeB?: TableViewPageSize
) => {
  if (!pageSizeA && !pageSizeB) return true;
  return isEqual(pageSizeA, pageSizeB);
};

export const paramsAreEquals = (
  paramsA?: TableViewParams,
  paramsB?: TableViewParams
) => {
  if (!sortIsEqual(paramsA?.sort, paramsB?.sort)) return false;

  if (!filtersIsEqual(paramsA?.filters, paramsB?.filters)) return false;

  if (!pageSizeIsEqual(paramsA?.pageSize, paramsB?.pageSize)) return false;

  return true;
};

export const getListParamsFromSearch = ({
  searchParams,
  storageKey,
  defaultSort,
  defaultFilters,
  defaultPageSize,
}: GetListParamsFromSearchProps): ListParams => {
  const sort = getSortSearchParam({
    searchParams,
    storageKey,
  });
  const currentPage = getCurrentPageSearchParam({
    searchParams,
    storageKey,
  });
  const pageSize = getPageSizeSearchParam({
    searchParams,
    storageKey,
  });
  const filters = getFiltersSearchParam({
    searchParams,
    storageKey,
  });

  return {
    sort: sort ?? defaultSort ?? [],
    filters: filters ?? defaultFilters ?? {},
    pagination: {
      current: currentPage ?? 1,
      pageSize: pageSize ?? defaultPageSize,
    },
  };
};

export const getSortSearchParam = ({
  searchParams,
  storageKey,
}: GetListParamProps): TableViewSort | undefined => {
  const sortKey = `${storageKey}.${SORT_KEY}`;

  if (!searchParams.has(sortKey)) return undefined;

  return searchParams.get(sortKey)?.split(",");
};

export const getFiltersSearchParam = ({
  searchParams,
  storageKey,
}: GetListParamProps): TableViewFilters | undefined => {
  const filters: TableViewFilters = {};

  searchParams.forEach((value, key) => {
    if (!key.includes(`${storageKey}.${FILTER_KEY}`) || !value) return;
    const filterKey = key
      .replace(`${storageKey}.`, "")
      .replace(/^filter\[/, "")
      .replace(/\]$/, "");

    filters[filterKey] = value.split(",");
  });

  if (Object.keys(filters).length <= 0) return undefined;

  return filters;
};

export const getCurrentPageSearchParam = ({
  searchParams,
  storageKey,
}: GetListParamProps): ListPageNumber | undefined => {
  const pageKey = `${storageKey}.${PAGE_KEY}`;

  if (!searchParams.has(pageKey)) return undefined;

  return parseInt(searchParams.get(pageKey) ?? "");
};

export const getPageSizeSearchParam = ({
  searchParams,
  storageKey,
}: GetListParamProps): TableViewPageSize | undefined => {
  const perPageKey = `${storageKey}.${PER_PAGE_KEY}`;

  if (!searchParams.has(perPageKey)) return undefined;

  return parseInt(searchParams.get(perPageKey) ?? "");
};

export const buildSearchParams = ({
  sort,
  filters,
  pageNumber,
  pageSize,
  searchParams,
  storageKey,
}: BuildSearchParamsProps) => {
  const existingParams = getUrlParamsNonRelatedToList({
    searchParams,
    storageKey,
  });

  const newParams = new URLSearchParams(existingParams);

  if (sort.length > 0) {
    newParams.set(`${storageKey}.${SORT_KEY}`, sort.join(","));
  }

  for (const [key, value] of Object.entries(filters)) {
    const filterKey = key.includes(FILTER_KEY) ? key : `${FILTER_KEY}[${key}]`;
    newParams.set(
      `${storageKey}.${filterKey}`,
      Array.isArray(value) ? value.join(",") : value
    );
  }

  if (pageNumber && pageNumber > 1)
    newParams.set(`${storageKey}.${PAGE_KEY}`, `${pageNumber}`);

  if (pageSize) newParams.set(`${storageKey}.${PER_PAGE_KEY}`, `${pageSize}`);

  return newParams.toString();
};

export const getUrlParamsNonRelatedToList = ({
  searchParams,
  storageKey,
}: GetListParamProps) => {
  const keepParams = new URLSearchParams();

  searchParams.forEach((value, key) => {
    if (!key.includes(storageKey)) {
      keepParams.set(key, value);
    }
  });

  return keepParams;
};
